import React, { useMemo, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  FaCalculator,
  FaCheckCircle,
  FaCopy,
  FaDownload,
  FaExclamationTriangle,
  FaRedo,
} from "react-icons/fa";
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap/dist/css/bootstrap.min.css';
const COLORS = {
  bg: "#F6F4F0",
  ink: "#17161B",
  panel: "#FFFFFF",
  dark: "#111014",
  accent: "#E8481D",
  accentSoft: "#FCE7DF",
  green: "#16845B",
  greenSoft: "#E7F6EF",
  yellow: "#A76500",
  yellowSoft: "#FFF4D6",
  muted: "#77737F",
  border: "#E7E3DC",
};

// USD per 1 million tokens. Verified against the official OpenAI API
// pricing page on 2026-08-30. Custom rates remain editable for any provider.
const MODEL_PRESETS = {
  custom: {
    name: "Custom pricing",
    input: 1,
    cachedInput: 0.1,
    cacheWrite: 0,
    output: 4,
    official: false,
  },
  "gpt-5.6-sol": {
    name: "GPT-5.6 Sol — standard short context",
    input: 4,
    cachedInput: 0.4,
    cacheWrite: 5,
    output: 20,
    official: true,
  },
  "gpt-5.6-terra": {
    name: "GPT-5.6 Terra — standard short context",
    input: 2,
    cachedInput: 0.2,
    cacheWrite: 2.5,
    output: 12,
    official: true,
  },
  "gpt-5.6-luna": {
    name: "GPT-5.6 Luna — standard short context",
    input: 0.2,
    cachedInput: 0.02,
    cacheWrite: 0.25,
    output: 1.2,
    official: true,
  },
  "gpt-5-mini": {
    name: "GPT-5 Mini",
    input: 0.25,
    cachedInput: 0.025,
    cacheWrite: 0,
    output: 2,
    official: true,
  },
  "chat-latest": {
    name: "chat-latest — standard",
    input: 5,
    cachedInput: 0.5,
    cacheWrite: 0,
    output: 30,
    official: true,
  },
  "gpt-5.3-codex": {
    name: "GPT-5.3 Codex — standard",
    input: 1.75,
    cachedInput: 0.175,
    cacheWrite: 0,
    output: 14,
    official: true,
  },
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
};

const calculateCosts = ({
  inputTokens,
  cachedTokens,
  cacheWriteTokens,
  outputTokens,
  requests,
  inputRate,
  cachedRate,
  cacheWriteRate,
  outputRate,
  toolCalls,
  toolRate,
  uplift,
  discount,
}) => {
  const input = toNumber(inputTokens);
  const cached = Math.min(input, toNumber(cachedTokens));
  const uncached = Math.max(0, input - cached);
  const requestCount = toNumber(requests);
  const perRequest = {
    input: (uncached / 1_000_000) * toNumber(inputRate),
    cached: (cached / 1_000_000) * toNumber(cachedRate),
    cacheWrite:
      (toNumber(cacheWriteTokens) / 1_000_000) * toNumber(cacheWriteRate),
    output: (toNumber(outputTokens) / 1_000_000) * toNumber(outputRate),
    tools: (toNumber(toolCalls) / 1000) * toNumber(toolRate),
  };
  const rawPerRequest = Object.values(perRequest).reduce(
    (sum, value) => sum + value,
    0
  );
  const adjustedPerRequest =
    rawPerRequest *
    (1 + toNumber(uplift) / 100) *
    (1 - Math.min(100, toNumber(discount)) / 100);

  return {
    uncachedTokens: uncached,
    cachedTokens: cached,
    perRequest,
    rawPerRequest,
    adjustedPerRequest,
    totalUsd: adjustedPerRequest * requestCount,
  };
};

const TokenCostCalculator = () => {
  const defaultPreset = MODEL_PRESETS["gpt-5.6-luna"];
  const [presetKey, setPresetKey] = useState("gpt-5.6-luna");
  const [inputTokens, setInputTokens] = useState("1000");
  const [cachedTokens, setCachedTokens] = useState("0");
  const [cacheWriteTokens, setCacheWriteTokens] = useState("0");
  const [outputTokens, setOutputTokens] = useState("500");
  const [requests, setRequests] = useState("1000");
  const [inputRate, setInputRate] = useState(String(defaultPreset.input));
  const [cachedRate, setCachedRate] = useState(String(defaultPreset.cachedInput));
  const [cacheWriteRate, setCacheWriteRate] = useState(
    String(defaultPreset.cacheWrite)
  );
  const [outputRate, setOutputRate] = useState(String(defaultPreset.output));
  const [toolCalls, setToolCalls] = useState("0");
  const [toolRate, setToolRate] = useState("0");
  const [uplift, setUplift] = useState("0");
  const [discount, setDiscount] = useState("0");
  const [currency, setCurrency] = useState("USD");
  const [exchangeRate, setExchangeRate] = useState("1");
  const [sampleText, setSampleText] = useState("");

  const applyPreset = (key) => {
    const preset = MODEL_PRESETS[key];
    setPresetKey(key);
    setInputRate(String(preset.input));
    setCachedRate(String(preset.cachedInput));
    setCacheWriteRate(String(preset.cacheWrite));
    setOutputRate(String(preset.output));
    console.log("[TOKEN CALCULATOR] Pricing preset applied.", {
      key,
      ...preset,
    });
  };

  const calculation = useMemo(
    () =>
      calculateCosts({
        inputTokens,
        cachedTokens,
        cacheWriteTokens,
        outputTokens,
        requests,
        inputRate,
        cachedRate,
        cacheWriteRate,
        outputRate,
        toolCalls,
        toolRate,
        uplift,
        discount,
      }),
    [
      inputTokens,
      cachedTokens,
      cacheWriteTokens,
      outputTokens,
      requests,
      inputRate,
      cachedRate,
      cacheWriteRate,
      outputRate,
      toolCalls,
      toolRate,
      uplift,
      discount,
    ]
  );

  const selectedPreset = MODEL_PRESETS[presetKey];
  const conversion = currency === "USD" ? 1 : toNumber(exchangeRate);
  const currencySymbol = currency === "USD" ? "$" : currency === "BDT" ? "৳" : "";
  const convertedTotal = calculation.totalUsd * conversion;
  const convertedPerRequest = calculation.adjustedPerRequest * conversion;
  const averageInputPerRequest = toNumber(inputTokens);
  const estimatedTextTokens = Math.ceil(sampleText.length / 4);
  const cachedTooHigh = toNumber(cachedTokens) > toNumber(inputTokens);
  const longContextWarning =
    presetKey.startsWith("gpt-5.6-") && averageInputPerRequest > 272000;

  const formatMoney = (value, symbol = currencySymbol) => {
    if (!Number.isFinite(value)) return `${symbol}0.00`;
    if (value === 0) return `${symbol}0.00`;
    if (Math.abs(value) < 0.000001) return `${symbol}${value.toExponential(4)}`;
    if (Math.abs(value) < 0.01) return `${symbol}${value.toFixed(6)}`;
    return `${symbol}${value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    })}`;
  };

  const costRows = [
    ["Uncached input", calculation.perRequest.input],
    ["Cached input", calculation.perRequest.cached],
    ["Cache writes", calculation.perRequest.cacheWrite],
    ["Output", calculation.perRequest.output],
    ["Tool calls", calculation.perRequest.tools],
  ];

  const comparison = useMemo(() => {
    return Object.entries(MODEL_PRESETS)
      .filter(([key]) => key !== "custom")
      .map(([key, preset]) => {
        const value = calculateCosts({
          inputTokens,
          cachedTokens,
          cacheWriteTokens,
          outputTokens,
          requests,
          inputRate: preset.input,
          cachedRate: preset.cachedInput,
          cacheWriteRate: preset.cacheWrite,
          outputRate: preset.output,
          toolCalls,
          toolRate,
          uplift,
          discount,
        });
        return { key, name: preset.name, totalUsd: value.totalUsd };
      })
      .sort((a, b) => a.totalUsd - b.totalUsd);
  }, [inputTokens, cachedTokens, cacheWriteTokens, outputTokens, requests, toolCalls, toolRate, uplift, discount]);

  const buildSummary = () =>
    [
      `Token Cost Estimate`,
      `Model/pricing: ${selectedPreset.name}`,
      `Input tokens per request: ${toNumber(inputTokens).toLocaleString()}`,
      `Cached input tokens per request: ${calculation.cachedTokens.toLocaleString()}`,
      `Cache-write tokens per request: ${toNumber(cacheWriteTokens).toLocaleString()}`,
      `Output tokens per request: ${toNumber(outputTokens).toLocaleString()}`,
      `Requests: ${toNumber(requests).toLocaleString()}`,
      `Cost per request: ${formatMoney(convertedPerRequest)}`,
      `Total estimated cost: ${formatMoney(convertedTotal)}`,
      `USD total: $${calculation.totalUsd.toFixed(8)}`,
      `Uplift: ${toNumber(uplift)}%`,
      `Discount: ${Math.min(100, toNumber(discount))}%`,
      `Pricing verified: 2026-08-30; verify current provider pricing before budgeting.`,
    ].join("\n");

  const copySummary = async () => {
    try {
      await navigator.clipboard.writeText(buildSummary());
      toast.success("Cost estimate copied!", { theme: "colored" });
    } catch (error) {
      console.error("[TOKEN CALCULATOR] Copy failed.", error);
      toast.error("Failed to copy the estimate.");
    }
  };

  const downloadCsv = () => {
    const csvRows = [
      ["Item", "Value"],
      ["Pricing preset", selectedPreset.name],
      ["Input tokens per request", toNumber(inputTokens)],
      ["Cached tokens per request", calculation.cachedTokens],
      ["Cache-write tokens per request", toNumber(cacheWriteTokens)],
      ["Output tokens per request", toNumber(outputTokens)],
      ["Requests", toNumber(requests)],
      ["USD cost per request", calculation.adjustedPerRequest],
      ["USD total", calculation.totalUsd],
      [`${currency} total`, convertedTotal],
    ];
    const csv = csvRows
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = "token-cost-estimate.csv";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
    toast.success("CSV downloaded!", { theme: "colored" });
  };

  const resetCalculator = () => {
    setInputTokens("1000");
    setCachedTokens("0");
    setCacheWriteTokens("0");
    setOutputTokens("500");
    setRequests("1000");
    setToolCalls("0");
    setToolRate("0");
    setUplift("0");
    setDiscount("0");
    setCurrency("USD");
    setExchangeRate("1");
    setSampleText("");
    applyPreset("gpt-5.6-luna");
    toast.info("Calculator reset.");
  };

  const numberInput = (label, value, setter, help = "") => (
    <div>
      <label className="form-label small fw-bold">{label}</label>
      <input
        type="number"
        min="0"
        step="any"
        className="form-control"
        value={value}
        onChange={(event) => setter(event.target.value)}
      />
      {help && <small style={{ color: COLORS.muted }}>{help}</small>}
    </div>
  );

  return (
    <div style={{ background: COLORS.bg, minHeight: "100vh", padding: "44px 16px" }}>
      <ToastContainer />
      <div style={{ maxWidth: 1040, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 27 }}>
          <div style={{ display: "inline-block", color: COLORS.accent, background: COLORS.accentSoft, borderRadius: 999, padding: "5px 11px", fontSize: 11, fontWeight: 800, letterSpacing: 0.7, marginBottom: 11 }}>FREE AI COST TOOL</div>
          <h1 style={{ color: COLORS.ink, fontWeight: 850, fontSize: 36, marginBottom: 7 }}>Token Cost Calculator</h1>
          <p style={{ color: COLORS.muted, margin: 0 }}>Estimate input, caching, output, tool, and volume costs.</p>
        </div>

        <div className="row g-3">
          <div className="col-lg-7">
            <div style={{ background: COLORS.panel, border: `1px solid ${COLORS.border}`, borderRadius: 15, padding: 22 }}>
              <label className="form-label small fw-bold">Pricing preset</label>
              <select className="form-select" value={presetKey} onChange={(event) => applyPreset(event.target.value)}>
                {Object.entries(MODEL_PRESETS).map(([key, preset]) => <option value={key} key={key}>{preset.name}</option>)}
              </select>
              <div style={{ color: COLORS.muted, fontSize: 11.5, marginTop: 6 }}>
                Prices are USD per 1M tokens. Rates remain editable below.
              </div>

              <hr style={{ borderColor: COLORS.border, margin: "19px 0" }} />
              <h6 style={{ color: COLORS.ink, fontWeight: 800 }}>Usage per request</h6>
              <div className="row g-3">
                <div className="col-md-6">{numberInput("Total input tokens", inputTokens, setInputTokens, "Cached input is included in this total.")}</div>
                <div className="col-md-6">{numberInput("Cached input tokens", cachedTokens, setCachedTokens)}</div>
                <div className="col-md-6">{numberInput("Cache-write tokens", cacheWriteTokens, setCacheWriteTokens)}</div>
                <div className="col-md-6">{numberInput("Output tokens", outputTokens, setOutputTokens)}</div>
                <div className="col-md-6">{numberInput("Number of requests", requests, setRequests)}</div>
                <div className="col-md-3">{numberInput("Tool calls/request", toolCalls, setToolCalls)}</div>
                <div className="col-md-3">{numberInput("$/1K tool calls", toolRate, setToolRate)}</div>
              </div>

              {cachedTooHigh && <div style={{ color: COLORS.yellow, background: COLORS.yellowSoft, borderRadius: 8, padding: "9px 11px", fontSize: 12, marginTop: 12 }}><FaExclamationTriangle size={11} style={{ marginRight: 6 }} />Cached tokens exceed total input, so they are capped at total input tokens.</div>}
              {longContextWarning && <div style={{ color: COLORS.yellow, background: COLORS.yellowSoft, borderRadius: 8, padding: "9px 11px", fontSize: 12, marginTop: 9 }}><FaExclamationTriangle size={11} style={{ marginRight: 6 }} />This request exceeds 272K input tokens. Select Custom pricing and enter the applicable long-context rates before budgeting.</div>}

              <hr style={{ borderColor: COLORS.border, margin: "19px 0" }} />
              <h6 style={{ color: COLORS.ink, fontWeight: 800 }}>Rates per 1M tokens</h6>
              <div className="row g-3">
                <div className="col-md-3">{numberInput("Input $", inputRate, (value) => { setInputRate(value); setPresetKey("custom"); })}</div>
                <div className="col-md-3">{numberInput("Cached $", cachedRate, (value) => { setCachedRate(value); setPresetKey("custom"); })}</div>
                <div className="col-md-3">{numberInput("Cache write $", cacheWriteRate, (value) => { setCacheWriteRate(value); setPresetKey("custom"); })}</div>
                <div className="col-md-3">{numberInput("Output $", outputRate, (value) => { setOutputRate(value); setPresetKey("custom"); })}</div>
                <div className="col-md-4">{numberInput("Regional uplift %", uplift, setUplift)}</div>
                <div className="col-md-4">{numberInput("Discount %", discount, setDiscount)}</div>
                <div className="col-md-4">
                  <label className="form-label small fw-bold">Currency</label>
                  <select className="form-select" value={currency} onChange={(event) => { setCurrency(event.target.value); if (event.target.value === "USD") setExchangeRate("1"); }}>
                    <option value="USD">USD ($)</option>
                    <option value="BDT">BDT (৳)</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="CUSTOM">Custom</option>
                  </select>
                </div>
                {currency !== "USD" && <div className="col-md-6">{numberInput(`1 USD = how many ${currency}?`, exchangeRate, setExchangeRate, "Enter the current exchange rate manually.")}</div>}
              </div>

              <hr style={{ borderColor: COLORS.border, margin: "19px 0" }} />
              <h6 style={{ color: COLORS.ink, fontWeight: 800 }}>Quick text estimate</h6>
              <textarea className="form-control" rows={4} value={sampleText} onChange={(event) => setSampleText(event.target.value)} placeholder="Paste text for a rough token estimate..." />
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginTop: 7, color: COLORS.muted, fontSize: 12 }}><span>{sampleText.length.toLocaleString()} characters ≈ {estimatedTextTokens.toLocaleString()} tokens</span><button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => { setInputTokens(String(estimatedTextTokens)); toast.success("Estimated tokens applied to input."); }}>Use estimate</button></div>
              <small style={{ color: COLORS.yellow }}>Approximation only: actual tokenization varies by model, language, and content.</small>

              <div style={{ display: "flex", gap: 9, flexWrap: "wrap", marginTop: 20 }}>
                <button type="button" onClick={copySummary} style={{ border: "none", borderRadius: 9, padding: "10px 16px", background: COLORS.accent, color: "#fff", fontWeight: 750 }}><FaCopy size={11} /> Copy Estimate</button>
                <button type="button" onClick={downloadCsv} style={{ border: `1px solid ${COLORS.border}`, borderRadius: 9, padding: "10px 15px", background: "#fff", color: COLORS.ink, fontWeight: 700 }}><FaDownload size={11} /> Download CSV</button>
                <button type="button" onClick={resetCalculator} style={{ border: `1px solid ${COLORS.border}`, borderRadius: 9, padding: "10px 15px", background: "#fff", color: COLORS.ink, fontWeight: 700 }}><FaRedo size={11} /> Reset</button>
              </div>
            </div>
          </div>

          <div className="col-lg-5">
            <div style={{ background: COLORS.dark, color: "#ECE9E2", borderRadius: 15, overflow: "hidden", position: "sticky", top: 16 }}>
              <div style={{ borderBottom: "1px solid rgba(255,255,255,0.09)", padding: "12px 16px", color: "#B7B4C0", fontSize: 11, fontWeight: 800, letterSpacing: 0.7 }}><FaCalculator size={11} style={{ marginRight: 6 }} />ESTIMATED COST</div>
              <div style={{ padding: 20 }}>
                <div style={{ color: "#B7B4C0", fontSize: 12 }}>Total for {toNumber(requests).toLocaleString()} request(s)</div>
                <div style={{ color: "#83E2BB", fontSize: 40, lineHeight: 1.2, fontWeight: 850, wordBreak: "break-word" }}>{formatMoney(convertedTotal)}</div>
                {currency !== "USD" && <div style={{ color: "#8F8B98", fontSize: 12 }}>${calculation.totalUsd.toFixed(6)} USD</div>}
                <div style={{ marginTop: 17, paddingTop: 15, borderTop: "1px solid rgba(255,255,255,0.09)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 9, fontSize: 12 }}><span style={{ color: "#B7B4C0" }}>Per request</span><strong>{formatMoney(convertedPerRequest)}</strong></div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 9, fontSize: 12 }}><span style={{ color: "#B7B4C0" }}>Per 1K requests</span><strong>{formatMoney(convertedPerRequest * 1000)}</strong></div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 9, fontSize: 12 }}><span style={{ color: "#B7B4C0" }}>Uncached input</span><strong>{calculation.uncachedTokens.toLocaleString()}</strong></div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}><span style={{ color: "#B7B4C0" }}>Cached input</span><strong>{calculation.cachedTokens.toLocaleString()}</strong></div>
                </div>

                <div style={{ marginTop: 18 }}>
                  <div style={{ color: "#B7B4C0", fontSize: 11, fontWeight: 800, marginBottom: 8 }}>PER-REQUEST BREAKDOWN (USD)</div>
                  {costRows.map(([label, value]) => (
                    <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 7 }}><span style={{ color: "#B7B4C0" }}>{label}</span><span>${value.toFixed(8)}</span></div>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ background: COLORS.panel, border: `1px solid ${COLORS.border}`, borderRadius: 15, padding: 18, marginTop: 14 }}>
              <h6 style={{ color: COLORS.ink, fontWeight: 800, marginBottom: 12 }}>Preset comparison</h6>
              {comparison.map((item, index) => (
                <button type="button" onClick={() => applyPreset(item.key)} key={item.key} style={{ width: "100%", border: "none", borderBottom: index === comparison.length - 1 ? "none" : `1px solid ${COLORS.border}`, background: "transparent", display: "flex", justifyContent: "space-between", gap: 10, padding: "9px 0", textAlign: "left" }}><span style={{ color: COLORS.muted, fontSize: 11.5 }}>{item.name}</span><strong style={{ color: COLORS.ink, fontSize: 12 }}>${item.totalUsd.toFixed(4)}</strong></button>
              ))}
            </div>

            <div style={{ color: COLORS.green, background: COLORS.greenSoft, borderRadius: 10, padding: "11px 13px", fontSize: 11.5, marginTop: 14 }}><FaCheckCircle size={10} style={{ marginRight: 6 }} />Cached tokens are subtracted from total input before the normal input rate is applied, preventing double billing.</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TokenCostCalculator;
